import RemoveIcon from "@mui/icons-material/Remove";
import TrackChangesIcon from "@mui/icons-material/TrackChanges";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import WarningIcon from "@mui/icons-material/Warning";

import { EVOTRUX_BLUE, EVOTRUX_GREEN } from "../../../../constants";

import type { SvgIconComponent } from "@mui/icons-material";

export const HIGHLY_COMPETITIVE_THRESHOLD = 5;
export const COMPETITIVE_THRESHOLD = 12;
export const IN_THE_BALLPARK_THRESHOLD = 25;

export const CompetitivenessTier = {
  HIGHLY_COMPETITIVE: "HIGHLY_COMPETITIVE",
  COMPETITIVE: "COMPETITIVE",
  IN_THE_BALLPARK: "IN_THE_BALLPARK",
  NOT_COMPETITIVE: "NOT_COMPETITIVE",
} as const;
export type CompetitivenessTier =
  (typeof CompetitivenessTier)[keyof typeof CompetitivenessTier];

export interface TierConfig {
  color: string;
  icon: SvgIconComponent;
  label: string;
  range: string;
  /** Short form of `range` for the band bar, where space is tight. */
  bandLabel: string;
  description: string;
}

/**
 * Every description is direction-neutral because the rating behind them is an
 * absolute distance from the booked price — a quote that far off could be under
 * or over, and the endpoint does not say which.
 */
export const TIER_CONFIG: Record<CompetitivenessTier, TierConfig> = {
  [CompetitivenessTier.HIGHLY_COMPETITIVE]: {
    color: EVOTRUX_GREEN,
    icon: TrackChangesIcon,
    label: "Highly Competitive",
    range: `Within ${HIGHLY_COMPETITIVE_THRESHOLD}%`,
    bandLabel: `±${HIGHLY_COMPETITIVE_THRESHOLD}%`,
    description: `Your quote was within ${HIGHLY_COMPETITIVE_THRESHOLD}% of the booked price.`,
  },
  [CompetitivenessTier.COMPETITIVE]: {
    color: EVOTRUX_BLUE,
    icon: TrendingUpIcon,
    label: "Competitive",
    range: `Within ${HIGHLY_COMPETITIVE_THRESHOLD + 1}-${COMPETITIVE_THRESHOLD}%`,
    bandLabel: `${HIGHLY_COMPETITIVE_THRESHOLD + 1}-${COMPETITIVE_THRESHOLD}%`,
    description: `Your quote was within ${HIGHLY_COMPETITIVE_THRESHOLD + 1}-${COMPETITIVE_THRESHOLD}% of the booked price.`,
  },
  [CompetitivenessTier.IN_THE_BALLPARK]: {
    color: "#ED6C02",
    icon: RemoveIcon,
    label: "In the Ballpark",
    range: `Within ${COMPETITIVE_THRESHOLD + 1}-${IN_THE_BALLPARK_THRESHOLD}%`,
    bandLabel: `${COMPETITIVE_THRESHOLD + 1}-${IN_THE_BALLPARK_THRESHOLD}%`,
    description: `Your quote was within ${COMPETITIVE_THRESHOLD + 1}-${IN_THE_BALLPARK_THRESHOLD}% of the booked price.`,
  },
  [CompetitivenessTier.NOT_COMPETITIVE]: {
    color: "#D32F2F",
    icon: WarningIcon,
    label: "Not Competitive",
    range: `${IN_THE_BALLPARK_THRESHOLD + 1}%+`,
    bandLabel: `${IN_THE_BALLPARK_THRESHOLD + 1}%+`,
    description: `Your quote was more than ${IN_THE_BALLPARK_THRESHOLD}% away from the booked price.`,
  },
};
